import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskApi } from '../services/api';
import { Task, TaskType } from '../types';
import { fmtDayTime, fmtGap, fmtTime, istDay, istToday, label } from '../lib';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Empty, Failed, LeadCell, Loading } from '../components/WorkBits';
import { purposeOf, leadName, TaskActions, useCallModal, useChatSheet, isCheckingCall, isOverdue } from './queueBits';
import { useCallWindows } from '../context/CallWindowsContext';

type Tab = 'open' | 'proposed';
const TYPES: (TaskType | 'all')[] = ['all', 'call', 'whatsapp', 'site_visit', 'other'];

/** Every piece of open work, and every AI proposal waiting on a decision.
 *
 *  Replaces the old Callbacks page: that rendered only `type=call`, which is
 *  one filter of this list rather than a screen of its own. */
const QueuePage: React.FC = () => {
  const navigate = useNavigate();
  const { call, modal } = useCallModal();
  const { chatLead, sheet } = useChatSheet();
  const windows = useCallWindows();
  const [tab, setTab] = useState<Tab>('open');
  const [type, setType] = useState<TaskType | 'all'>('all');
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setTasks(null);
    setError(null);
    try {
      // Whose work: the sidebar scope pick, sent as a header by the client.
      const res = await taskApi.list({
        status: tab,
        ...(type !== 'all' ? { type } : {}),
      });
      setTasks(res.tasks);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [tab, type]);

  useEffect(() => { load(); }, [load]);

  const decide = async (t: Task, verb: 'approve' | 'reject') => {
    await (verb === 'approve' ? taskApi.approve(t.id) : taskApi.reject(t.id, 'rejected in portal'));
    load();
  };

  const today = istToday();
  const groups: { title: string; rows: Task[] }[] = tasks
    ? [
      { title: 'Overdue', rows: tasks.filter((t) => isOverdue(t, windows)) },
      // Past its day but not overdue: a dial still being checked, or a proposal
      // whose suggested time has gone by — still waiting on someone.
      { title: 'Earlier, still open', rows: tasks.filter((t) => !isOverdue(t, windows) && istDay(t.due_at) < today) },
      { title: 'Later today', rows: tasks.filter((t) => !isOverdue(t, windows) && istDay(t.due_at) === today) },
      { title: 'Upcoming', rows: tasks.filter((t) => istDay(t.due_at) > today) },
    ].filter((g) => g.rows.length > 0)
    : [];

  const strip: StripItem[] = tasks
    ? [
      { v: tasks.filter((t) => isOverdue(t, windows)).length, k: 'overdue', cls: 'bad' },
      { v: tasks.filter((t) => istDay(t.due_at) === today).length, k: 'due today' },
      { v: tasks.length, k: tab === 'proposed' ? 'proposed' : 'open' },
    ]
    : [];

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">Queue</h1>
          <p className="page-sub">Open work on the leads you are viewing · ordered by when it is due · times IST</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3.5">
        <button className="pill" aria-pressed={tab === 'open'} onClick={() => setTab('open')}>Open</button>
        <button className="pill" aria-pressed={tab === 'proposed'} onClick={() => setTab('proposed')}>
          Proposed
        </button>
        <span className="flex-1" />
        <select className="inp" value={type} onChange={(e) => setType(e.target.value as TaskType | 'all')}>
          {TYPES.map((t) => (
            <option key={t} value={t}>{t === 'all' ? 'All types' : label(t)}</option>
          ))}
        </select>
      </div>

      {tab === 'proposed' && (
        <p className="text-graphite text-[12.5px] mb-3.5">
          Suggested from call summaries. Approving puts the task in the live queue; rejecting closes it
          for good — neither decision is ever re-run by the syncs.
        </p>
      )}

      {error && <Failed what="the queue" error={error} />}
      {!error && !tasks && <Loading what="the queue" />}

      {tasks && tasks.length > 0 && <div className="card mb-3.5"><NumberStrip items={strip} /></div>}

      {tasks && tasks.length === 0 && (
        <Empty>{tab === 'proposed' ? 'Nothing waiting on a decision.' : 'Nothing open here.'}</Empty>
      )}

      {groups.map(({ title, rows }) => (
        <div key={title}>
          <div className="groupline">{title}</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Lead</th><th>Due</th><th>Type</th><th>Purpose</th><th>Source</th>
                  <th className="text-right">{tab === 'proposed' ? 'Decision' : 'Reach'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/leads/${t.lead_id}`)}>
                    <td><LeadCell name={leadName(t)} sub={t.lead?.mobile} /></td>
                    <td>
                      {isCheckingCall(t) ? (
                        <span className="text-forest text-[12.5px] font-medium">Checking call…</span>
                      ) : (
                        <>
                          <span className={`num ${isOverdue(t, windows) ? 'text-brick font-semibold' : ''}`}>
                            {istDay(t.due_at) === today ? fmtTime(t.due_at) : fmtDayTime(t.due_at)}
                          </span>
                          <span className="block text-graphite text-[11.5px]">{fmtGap(t.due_at)}</span>
                        </>
                      )}
                    </td>
                    <td><span className="chip chip-mute">{label(t.type)}</span></td>
                    <td className="max-w-xs truncate" title={purposeOf(t) || ''}>{purposeOf(t) || '—'}</td>
                    <td className="text-graphite text-[12.5px]">{t.source}</td>
                    <td>
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        {tab === 'proposed' ? (
                          <span className="flex gap-1.5">
                            <button className="btn btn-pri btn-sm" onClick={() => decide(t, 'approve')}>Approve</button>
                            <button className="btn btn-sm" onClick={() => decide(t, 'reject')}>Reject</button>
                          </span>
                        ) : (
                          <TaskActions
                            task={t}
                            onCall={() => call(t.lead)}
                            onChat={() => chatLead(t.lead)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {modal}
      {sheet}
    </div>
  );
};

export default QueuePage;
